import { EntradasMapper } from '@/domain/simulador/entradas-mapper'
import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import type { IParcela } from '@/domain/simulador/gerenciador-pos-conclusao.interface'
import type { TSimulacaoSchema } from '@/domain/simulador/types/simulacao.type'
import { IntermediariasMapper, MensaisMapper } from '@/features/simulador/brasil/domain'
import { Arquivo } from '@/features/simulador/shared/domain/arquivo'
import { CalculadoraDeComissao } from '@/features/simulador/shared/domain/calculadora-de-comissao'
import { CalculadoraDeMargem } from '@/features/simulador/shared/domain/calculadora-de-margem'
import { CalculadoraDeParcelasBase } from '@/features/simulador/shared/domain/calculadora-de-parcelas'
import { CalculadoraDeValorMedio } from '@/features/simulador/shared/domain/calculadora-de-valor-medio'
import { CalculadoraDeValorPresenteBase } from '@/features/simulador/shared/domain/calculadora-de-valor-presente'
import { ConversorJuros } from '@/features/simulador/shared/domain/conversor-juros'
import { type Fator, Mascara, type Mascaras } from '@/features/simulador/shared/domain/mascara'
import { Rotina } from '@/features/simulador/shared/domain/rotina'

export type TSimulacaoCalculoPrice = {
  dataSimulacao: string
  entradas: TSimulacaoSchema<'Price'>['entradas']
  mensais: TSimulacaoSchema<'Price'>['mensais']
  intermediarias: TSimulacaoSchema<'Price'>['intermediarias']
  valorProposto: number
}

type TParametrosRotinaArquivoPrice = Omit<
  TParametrosRotinaArquivo,
  | 'dataSaldo'
  | 'percentualAutonomiaVendedorSobrePoupanca1Por3'
  | 'percentualAutonomiaVendedorSobrePoupanca2Por3'
  | 'percentualAutonomiaVendedorSobrePoupanca3Por3'
  | 'percentualINCC'
  | 'margemVendedor1Por3'
  | 'margemVendedor2Por3'
  | 'margemVendedor3Por3'
  | 'margemGcom'
  | 'margemGR'
>

type TValoresAVista = Pick<TParametrosRotinaArquivo, 'valorAVistaVendedor' | 'valorAVistaGcom' | 'valorAVistaGr'>

type TParametrosMontarArquivo = Pick<
  TParametrosRotinaArquivo,
  'mascaraGcom' | 'mascaraGR' | 'ordemMascaraGcom' | 'ordemMascaraGR'
>
export class RotinaArquivoPrice {
  private readonly calculadoraComissao: CalculadoraDeComissao
  constructor(
    readonly parametros: TParametrosRotinaArquivoPrice,
    private readonly calculadoraValorPresente: CalculadoraDeValorPresenteBase = new CalculadoraDeValorPresenteBase(),
    private readonly calculadoraParcelas: CalculadoraDeParcelasBase = new CalculadoraDeParcelasBase(),
    private readonly calculadoraDeMargem: CalculadoraDeMargem = new CalculadoraDeMargem(),
    private readonly mascara: Mascara = new Mascara(),
  ) {
    this.calculadoraComissao = new CalculadoraDeComissao(this.parametros.percentualComissaoVendedor)
  }

  calcular = (grupos: TSimulacaoCalculoPrice) => {
    const taxas = this.etapa1CalculoTaxaDeJuros()
    const parcelasIniciais = this.obterParcelas(grupos)

    const parcelasComValorPresente = this.etapa2CalcularValorPresente(
      parcelasIniciais,
      grupos.dataSimulacao,
      taxas.depoisPontoMedio,
    )

    const valorPresenteTotal = this.etapa3SomatorioValorPresenteParcelas(parcelasComValorPresente)

    const comissaoVendedor = this.etapa4CalcularComissaoDoVendedor(valorPresenteTotal)

    const parcelasComImposto = this.etapa5CalcularImpostoPorParcela(parcelasComValorPresente)
    const parcelasSemComissaoEImposto = this.etapa6DescontarComissaoEImpostos(parcelasComImposto, comissaoVendedor)

    const parcelasNoPontoMedio = this.etapa7CalcularParcelasNoPontoMedio(
      parcelasSemComissaoEImposto,
      taxas.antesPontoMedio,
      taxas.depoisPontoMedio,
    )

    const valorPontoMedioTotal = this.etapa8ObterValorMedioTotal(parcelasNoPontoMedio)
    const valorPontoMedioTotalUPF = this.etapa9ConverterValorMedioTotalEmUPF(valorPontoMedioTotal)

    const margemBrutaEmUPF = this.etapa10CalculoMargemBruta(valorPontoMedioTotalUPF)
    const margemLiquidaEmUPF = this.etapa11CalculoMargemLiquidaEmUPF(margemBrutaEmUPF)

    const fatores = this.etapa12CalcularFatorAlcancado(grupos.valorProposto, {
      valorAVistaVendedor: this.parametros.valorAVistaVendedor,
      valorAVistaGcom: this.parametros.valorAVistaGcom,
      valorAVistaGr: this.parametros.valorAVistaGr,
    })

    const mascarasAplicadas = this.etapa13CalcularMascaraAplicada(
      {
        mascaraVendedor: this.parametros.mascaraVendedor,
        mascaraGcom: this.parametros.mascaraGcom,
        mascaraGR: this.parametros.mascaraGR,
      },
      fatores,
    )

    const valorAVistaNoPontoMedio = this.etapa14CalcularPrecoAVistaNoPontoMedio(
      valorPresenteTotal,
      grupos.dataSimulacao,
      taxas.depoisPontoMedio,
      this.parametros.dataPontoMedio,
    )

    const valorAVistaNoPontoMedioUPF = this.etapa15ConverterPrecoAVistaNoPontoMedioParaUPF(
      valorAVistaNoPontoMedio,
      this.parametros.taxaUpf,
    )
    const percentualMargemVenda = this.etapa16CalcularPercentualMargemVenda(
      margemLiquidaEmUPF,
      valorAVistaNoPontoMedioUPF,
    )
    const rotina = this.etapa17MontarNumeroDaRotina(
      mascarasAplicadas.mascaraVendedor,
      percentualMargemVenda,
      this.parametros.ordemMascaraVendedor,
    )

    const arquivo = this.etapa18MontarArquivo(valorPresenteTotal, {
      mascaraGcom: mascarasAplicadas.mascaraGcom,
      mascaraGR: mascarasAplicadas.mascaraGR,
      ordemMascaraGcom: this.parametros.ordemMascaraGcom,
      ordemMascaraGR: this.parametros.ordemMascaraGR,
    })

    return {
      comissaoVendedor: Number(comissaoVendedor.toFixed(2)),
      valorMedioTotal: Number(valorPontoMedioTotal.toFixed(2)),
      valorMedioTotalUPF: Number(valorPontoMedioTotalUPF.toFixed(2)),
      margemBruta: Number(margemBrutaEmUPF.toFixed(2)),
      margemLiquida: Number(margemLiquidaEmUPF.toFixed(2)),
      fatorGcom: Number(fatores.fatorGcom.toFixed(3)),
      fatorGR: Number(fatores.fatorGR.toFixed(3)),
      fatorVendedorPrazoDePagamento: Number(fatores.fatorVendedor.toFixed(3)),
      percentualMargemVenda: Number(percentualMargemVenda.toFixed(2)),
      valorPresente: Number(valorPresenteTotal.toFixed(2)),
      valorPresentePontoMedio: Number(valorPontoMedioTotal.toFixed(2)),
      valorPresentePontoMedioUPF: Number(valorPontoMedioTotalUPF.toFixed(2)),
      fatorVendedor1Por3: Number(fatores.fatorVendedor.toFixed(3)),
      fatorVendedor2Por3: Number(fatores.fatorVendedor.toFixed(3)),
      fatorVendedor3Por3: Number(fatores.fatorVendedor.toFixed(3)),
      arquivo,
      rotina,
    }
  }

  private etapa1CalculoTaxaDeJuros() {
    return {
      antesPontoMedio: ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
        this.parametros.percentualJurosAnualAntesPontoMedio,
      ),
      depoisPontoMedio: ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
        this.parametros.percentualJurosAnualDepoisPontoMedio,
      ),
    }
  }

  private etapa2CalcularValorPresente(
    parcelas: IParcela[],
    dataSimulacao: string,
    taxaJurosDiariaDepoisPontoMedio: number,
  ) {
    return this.calculadoraValorPresente.calcularValorPresenteDasParcelas(
      parcelas,
      dataSimulacao,
      taxaJurosDiariaDepoisPontoMedio,
    )
  }

  private obterParcelas(grupos: TSimulacaoCalculoPrice) {
    const entradas = EntradasMapper.transform(grupos.entradas?.grupos ?? [])
    const mensais = MensaisMapper.transformPrice(grupos.mensais)
    const intermediarias = IntermediariasMapper.transformPrice(grupos.intermediarias)

    const gruposParcelas = { entradas, mensais, intermediarias }
    const tiposParcelas = ['entradas', 'mensais', 'intermediarias'] as const

    return tiposParcelas.flatMap((tipo) =>
      gruposParcelas[tipo].flatMap((grupo) => grupo.parcelas.map((parcela) => ({ ...parcela, tipo }))),
    )
  }

  private etapa3SomatorioValorPresenteParcelas(parcelasComValorPresente: IParcela[]) {
    return this.calculadoraParcelas.somar(parcelasComValorPresente, 'valorPresente')
  }

  private etapa4CalcularComissaoDoVendedor(valorPresente: number) {
    return this.calculadoraComissao.calcularComissaoVendedor(valorPresente)
  }
  private etapa5CalcularImpostoPorParcela(parcelasComValorPresente: IParcela[]) {
    return this.calculadoraParcelas.calcularValorDeImpostoDasParcelas(
      parcelasComValorPresente,
      this.parametros.percentualImpostos,
      this.parametros.percentualISS,
      'valor',
    )
  }

  private etapa6DescontarComissaoEImpostos(parcelasComImposto: IParcela[], comissaoVendedor: number) {
    return this.calculadoraParcelas.calcularValorSemImpostoEComissaoDasParcelas(
      parcelasComImposto,
      comissaoVendedor,
      'valor',
    )
  }

  private etapa7CalcularParcelasNoPontoMedio(
    parcelasSemComissaoEImposto: IParcela[],
    taxaJurosDiariaAntesPontoMedio: number,
    taxaJurosDiariaDepoisPontoMedio: number,
  ) {
    return this.calculadoraParcelas.calcularValorDePontoMedioDasParcelas(
      parcelasSemComissaoEImposto,
      taxaJurosDiariaAntesPontoMedio,
      taxaJurosDiariaDepoisPontoMedio,
      this.parametros.dataPontoMedio,
    )
  }
  private etapa8ObterValorMedioTotal(parcelasComPontoMedio: IParcela[]) {
    return this.calculadoraParcelas.somar(parcelasComPontoMedio, 'valorPontoMedio')
  }

  private etapa9ConverterValorMedioTotalEmUPF(valorMedioTotal: number) {
    return CalculadoraDeValorMedio.calcularEmUPF(valorMedioTotal, this.parametros.taxaUpf)
  }
  private etapa10CalculoMargemBruta(valorMedioTotalUPF: number) {
    return this.calculadoraDeMargem.calcularMargemBrutaEmUPF(valorMedioTotalUPF, this.parametros.custoEmUPF)
  }

  private etapa11CalculoMargemLiquidaEmUPF(margemBrutaEmUPF: number) {
    return this.calculadoraDeMargem.calcularMargemLiquidaEmUPF(
      margemBrutaEmUPF,
      this.parametros.patrimonioAfetacao,
      this.parametros.modoDeLucroDaEmpresa,
      this.parametros.percentualIRPJ,
    )
  }

  private etapa12CalcularFatorAlcancado(valorProposto: number, valoresAVista: TValoresAVista) {
    const fatorVendedor = valoresAVista.valorAVistaVendedor ? valorProposto / valoresAVista.valorAVistaVendedor : 0
    const fatorGcom = valoresAVista.valorAVistaGcom ? valorProposto / valoresAVista.valorAVistaGcom : 0
    const fatorGR = valoresAVista.valorAVistaGr ? valorProposto / valoresAVista.valorAVistaGr : 0

    return {
      fatorVendedor,
      fatorGcom,
      fatorGR,
    }
  }

  private etapa13CalcularMascaraAplicada(mascaras: Mascaras, fatoresCalculados: Fator) {
    return this.mascara.aplicarMascaraNosFatores(fatoresCalculados, mascaras)
  }

  private etapa14CalcularPrecoAVistaNoPontoMedio(
    valorPresente: number,
    dataSimulacao: string,
    taxaJurosDiariaDepoisPontoMedio: number,
    dataPontoMedio: string,
  ) {
    return this.calculadoraValorPresente.calcularValorPresenteNoPontoMedio({
      valorPresente,
      taxaJurosDiarioDepoisPontoMedio: taxaJurosDiariaDepoisPontoMedio,
      dataSimulacao,
      dataPontoMedio,
    })
  }

  private etapa15ConverterPrecoAVistaNoPontoMedioParaUPF(valorPresentePontoMedio: number, taxaUPF: number) {
    return this.calculadoraValorPresente.calcularValorPresenteNoPontoMedioEmUPF(valorPresentePontoMedio, taxaUPF)
  }

  private etapa16CalcularPercentualMargemVenda(margemLiquidaEmUPF: number, valorPresentePontoMedioUPF: number) {
    return this.calculadoraDeMargem.calcularPorcentagemMargemDeVenda(margemLiquidaEmUPF, valorPresentePontoMedioUPF)
  }

  private etapa17MontarNumeroDaRotina(
    mascaraVendedor: number,
    percentualMargemVenda: number,
    ordemMascaraVendedor: number,
  ) {
    return Rotina.montar({
      ordemMascaraVendedor: ordemMascaraVendedor,
      mascaraVendedor,
      percentualMargemVenda,
    })
  }

  private etapa18MontarArquivo(valorPresente: number, mascaras: TParametrosMontarArquivo) {
    return Arquivo.montar(this.mascara, {
      ordemMascaraGcom: mascaras.ordemMascaraGcom,
      ordemMascaraGR: mascaras.ordemMascaraGR,
      valorPresente,
      mascaraGcom: mascaras.mascaraGcom,
      mascaraGR: mascaras.mascaraGR,
    })
  }
}
